import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

interface Tag { id: string; name: string; color: string; }
interface Contact {
  id: string; first_name: string; last_name: string; email: string;
  phone: string | null; relationship: string | null; subscribed: number; tags?: Tag[];
}
type Form = { first_name: string; last_name: string; email: string; phone: string; relationship: string; subscribed: boolean; tag_ids: string[] };
const blankForm = (): Form => ({ first_name: '', last_name: '', email: '', phone: '', relationship: '', subscribed: true, tag_ids: [] });

/**
 * <cadence-mailing-page> — the Mailing section. First slice ships the **Contacts** tab
 * (external, non-student mailing audience: CRUD + tags + subscribe status). The Lists
 * and Emails tabs are placeholders for the next slices. Reads gated by mailing.view,
 * contact mutations by mailing.manage (enforced server-side).
 */
@customElement('cadence-mailing-page')
export class CadenceMailingPage extends LitElement {
  @state() private tab = 'contacts';
  @state() private contacts: Contact[] = [];
  @state() private tags: Tag[] = [];
  @state() private loading = true;
  @state() private search = '';
  @state() private tagFilter = '';
  // Edit/create modal
  @state() private editing = false;
  @state() private editId: string | null = null;
  @state() private form: Form = blankForm();
  @state() private saving = false;

  private readonly canManage = store.hasPermission('mailing.manage');
  private debounce = 0;

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-4); gap: 12px; flex-wrap: wrap; }
    h1 { margin: 0; font-size: 1.6rem; }
    .toolbar { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; margin-bottom: var(--space-4); }
    .toolbar cadence-input { min-width: 220px; }
    .grow { flex: 1; }
    .chips { display: inline-flex; gap: 4px; flex-wrap: wrap; }
    .chip { display: inline-flex; align-items: center; gap: 5px; font-size: 0.74rem; padding: 1px 8px; border-radius: 999px; border: 1px solid var(--color-border); color: var(--color-text-secondary); }
    .chip .dot { width: 8px; height: 8px; border-radius: 50%; }
    .efield { margin-bottom: var(--space-3); }
    .efield label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); }
    @media (max-width: 560px) { .grid2 { grid-template-columns: 1fr; } }
    .tagpick { display: flex; gap: 6px; flex-wrap: wrap; }
    .tagtoggle { cursor: pointer; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 999px; padding: 4px 12px; font: inherit; font-size: 0.82rem; color: var(--color-text-secondary); display: inline-flex; align-items: center; gap: 6px; }
    .tagtoggle .dot { width: 9px; height: 9px; border-radius: 50%; }
    .tagtoggle[aria-pressed='true'] { border-color: var(--color-primary); color: var(--color-primary); background: color-mix(in srgb, var(--color-primary) 8%, transparent); }
    .muted { color: var(--color-text-secondary); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.loadTags(); this.loadContacts(); }

  private async loadTags() {
    try { this.tags = await api.get<Tag[]>('/tags'); } catch { /* non-fatal */ }
  }
  private async loadContacts() {
    this.loading = true;
    const params = new URLSearchParams();
    if (this.search.trim().length >= 2) params.set('search', this.search.trim());
    if (this.tagFilter) params.set('tag', this.tagFilter);
    try {
      this.contacts = await api.get<Contact[]>(`/contacts${params.toString() ? `?${params}` : ''}`);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private onSearch(v: string) {
    this.search = v;
    clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => this.loadContacts(), 250);
  }

  private openNew() { this.editId = null; this.form = blankForm(); this.editing = true; }
  private openEdit(c: Contact) {
    this.editId = c.id;
    this.form = {
      first_name: c.first_name, last_name: c.last_name, email: c.email,
      phone: c.phone ?? '', relationship: c.relationship ?? '',
      subscribed: c.subscribed === 1, tag_ids: (c.tags ?? []).map((tag) => tag.id),
    };
    this.editing = true;
  }
  private toggleFormTag(id: string) {
    const has = this.form.tag_ids.includes(id);
    this.form = { ...this.form, tag_ids: has ? this.form.tag_ids.filter((tid) => tid !== id) : [...this.form.tag_ids, id] };
  }

  private async save() {
    if (!this.form.first_name.trim() || !this.form.last_name.trim()) { showToast(t('mailing.toast.contactNameRequired'), 'error'); return; }
    if (!this.form.email.trim()) { showToast(t('mailing.toast.contactEmailRequired'), 'error'); return; }
    this.saving = true;
    const body = {
      first_name: this.form.first_name.trim(), last_name: this.form.last_name.trim(),
      email: this.form.email.trim(), phone: this.form.phone.trim(), relationship: this.form.relationship.trim(),
      subscribed: this.form.subscribed, tag_ids: this.form.tag_ids,
    };
    try {
      if (this.editId) await api.put(`/contacts/${this.editId}`, body);
      else await api.post('/contacts', body);
      this.editing = false;
      await this.loadContacts();
      showToast(t('mailing.toast.contactSaved'), 'success');
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('mailing.toast.contactSaveFailed'), 'error'); }
    finally { this.saving = false; }
  }
  private async del(c: Contact) {
    if (!confirm(t('mailing.toast.confirmDeleteContact', { name: `${c.first_name} ${c.last_name}` }))) return;
    try { await api.del(`/contacts/${c.id}`); await this.loadContacts(); showToast(t('mailing.toast.contactDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private renderContacts() {
    const tagOptions = [{ value: '', label: t('mailing.page.allTags') }, ...this.tags.map((tag) => ({ value: tag.id, label: tag.name }))];
    return html`
      <div class="toolbar">
        <cadence-input label=${t('mailing.page.search')} placeholder=${t('mailing.page.searchPlaceholder')} .value=${this.search}
          @input=${(e: CustomEvent) => this.onSearch(e.detail)}></cadence-input>
        ${this.tags.length ? html`<cadence-select label=${t('mailing.page.tag')} .value=${this.tagFilter} .options=${tagOptions}
          @change=${(e: CustomEvent) => { this.tagFilter = e.detail; this.loadContacts(); }}></cadence-select>` : ''}
        <div class="grow"></div>
        ${this.canManage ? html`<cadence-button @click=${() => this.openNew()}>${t('mailing.page.newContact')}</cadence-button>` : ''}
      </div>

      ${this.loading
        ? html`<cadence-loading></cadence-loading>`
        : this.contacts.length
          ? html`<cadence-table .columns=${this.columns()} .rows=${this.contacts}></cadence-table>`
          : html`<cadence-empty-state heading=${t('mailing.page.emptyHeading')}
              message=${this.canManage ? t('mailing.page.emptyManage') : t('mailing.page.emptyPlain')}></cadence-empty-state>`}
    `;
  }

  private columns() {
    const cols: any[] = [
      { key: 'name', label: t('mailing.columns.name'), render: (r: Contact) => html`${r.first_name} ${r.last_name}` },
      { key: 'email', label: t('mailing.columns.email') },
      { key: 'phone', label: t('mailing.columns.phone'), render: (r: Contact) => r.phone || html`<span class="muted">—</span>` },
      { key: 'relationship', label: t('mailing.columns.relationship'), render: (r: Contact) => r.relationship || html`<span class="muted">—</span>` },
      { key: 'tags', label: t('mailing.columns.tags'), render: (r: Contact) => html`<span class="chips">${(r.tags ?? []).map((tag) => html`<span class="chip"><span class="dot" style="background:${tag.color}"></span>${tag.name}</span>`)}</span>` },
      { key: 'subscribed', label: t('mailing.columns.subscribed'), render: (r: Contact) => r.subscribed
          ? html`<cadence-badge color="var(--color-success)">${t('mailing.page.subscribed')}</cadence-badge>`
          : html`<cadence-badge color="var(--color-text-secondary)">${t('mailing.page.unsubscribed')}</cadence-badge>` },
    ];
    if (this.canManage) {
      cols.push({ key: 'id', label: '', render: (r: Contact) => html`<div style="display:flex;gap:6px;justify-content:flex-end">
        <cadence-button variant="ghost" @click=${() => this.openEdit(r)}>${t('mailing.page.edit')}</cadence-button>
        <cadence-button variant="danger" @click=${() => this.del(r)}>${t('mailing.page.delete')}</cadence-button>
      </div>` });
    }
    return cols;
  }

  render() {
    const tabs = [
      { id: 'contacts', label: t('mailing.page.tabs.contacts') },
      { id: 'lists', label: t('mailing.page.tabs.lists') },
      { id: 'emails', label: t('mailing.page.tabs.emails') },
    ];
    return html`
      <div class="head"><h1>${t('mailing.page.title')}</h1></div>
      <cadence-tabs .tabs=${tabs} active=${this.tab} @tab-change=${(e: CustomEvent) => (this.tab = e.detail)}></cadence-tabs>
      ${this.tab === 'contacts' ? this.renderContacts() : ''}
      ${this.tab === 'lists' ? html`<cadence-mailing-lists></cadence-mailing-lists>` : ''}
      ${this.tab === 'emails' ? html`<cadence-mailing-emails></cadence-mailing-emails>` : ''}

      <cadence-modal ?open=${this.editing} heading=${this.editId ? t('mailing.page.editContact') : t('mailing.page.newContact')} @close=${() => (this.editing = false)}>
        <div class="grid2">
          <div class="efield"><cadence-input label=${t('mailing.page.firstName')} .value=${this.form.first_name} @input=${(e: CustomEvent) => (this.form = { ...this.form, first_name: e.detail })}></cadence-input></div>
          <div class="efield"><cadence-input label=${t('mailing.page.lastName')} .value=${this.form.last_name} @input=${(e: CustomEvent) => (this.form = { ...this.form, last_name: e.detail })}></cadence-input></div>
        </div>
        <div class="efield"><cadence-input label=${t('mailing.page.email')} type="email" .value=${this.form.email} @input=${(e: CustomEvent) => (this.form = { ...this.form, email: e.detail })}></cadence-input></div>
        <div class="grid2">
          <div class="efield"><cadence-input label=${t('mailing.page.phoneOptional')} .value=${this.form.phone} @input=${(e: CustomEvent) => (this.form = { ...this.form, phone: e.detail })}></cadence-input></div>
          <div class="efield"><cadence-input label=${t('mailing.page.relationshipOptional')} placeholder=${t('mailing.page.relationshipPlaceholder')} .value=${this.form.relationship} @input=${(e: CustomEvent) => (this.form = { ...this.form, relationship: e.detail })}></cadence-input></div>
        </div>
        ${this.tags.length ? html`<div class="efield">
          <label>${t('mailing.page.tags')}</label>
          <div class="tagpick">
            ${this.tags.map((tag) => html`<button type="button" class="tagtoggle" aria-pressed=${this.form.tag_ids.includes(tag.id) ? 'true' : 'false'}
              @click=${() => this.toggleFormTag(tag.id)}><span class="dot" style="background:${tag.color}"></span>${tag.name}</button>`)}
          </div>
        </div>` : ''}
        ${this.editId ? html`<div class="efield">
          <cadence-toggle label=${t('mailing.page.subscribedToggle')} ?checked=${this.form.subscribed}
            @change=${(e: CustomEvent) => (this.form = { ...this.form, subscribed: e.detail })}></cadence-toggle>
        </div>` : ''}
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.editing = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.save()}>${t('mailing.page.saveContact')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
